"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.REDACTED = void 0;
exports.isGuid = isGuid;
exports.isSensitiveKey = isSensitiveKey;
exports.redactTelemetryText = redactTelemetryText;
function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t.return || t.return(); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
/**
 * PII / credential redaction for free text bound for telemetry (APM errors, labels, console logs).
 *
 * Scope (per the security team): emails, phone numbers, names, free-text shopper content,
 * credentials, and numeric shopperIds (usable as a login username). GUIDs (incl. customerId) and IP
 * addresses are NOT PII and are preserved.
 *
 * Pure and dependency-free so it is safe for the isolated `loader/hooks` subpath. Never throws.
 * NOTE: no regex lookbehind — it is a parse-time SyntaxError on Safari < 16.4.
 */

/* eslint-disable no-control-regex -- NUL (\u0000) is the placeholder delimiter for protected values */

var REDACTED = exports.REDACTED = '[REDACTED]';

// Bounds regex work on pathological input; telemetry text is truncated beyond this.
var MAX_TEXT_LENGTH = 16384;
var GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
var IPV4_OCTET = '(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)';

// Swapped for placeholders before redaction so the phone/key rules can't mangle them. Each pattern
// captures (prefix, value); only the value is protected. An IPv4 must have valid octets, exactly four
// groups and no leading `+`, so dotted phone numbers like `+61.412.345.678` aren't mistaken for one.
var PROTECTED_PATTERNS = [/()\b([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\b/gi,
// GUID
new RegExp("(^|[^\\w+.])((?:".concat(IPV4_OCTET, "\\.){3}").concat(IPV4_OCTET, ")(?!\\.?\\d)"), 'g'),
// IPv4
/()\b(\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?)\b/g // ISO date/datetime
];
var PLACEHOLDER = /\u0000(\d+)\u0000/g;
var PLACEHOLDER_ONLY = /^["']?\u0000\d+\u0000["']?$/;

// Includes URL-encoded (%40) and double-encoded (%2540) "@", as seen in leaked `initialPrompt=` values.
var EMAIL = /[A-Z0-9._%+-]{1,64}(?:@|%40|%2540)[A-Z0-9-]{1,63}(?:\.[A-Z0-9-]{1,63})*\.[A-Z]{2,24}/gi;
var JWT = /\beyJ[\w-]{5,}\.[\w-]{5,}\.[\w-]+/g;
var AUTH_SCHEME = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi;

// `key: value` / `key=value` / `"key":"value"` where the key names PII. Bare `name` is deliberately
// excluded (it would hit `name: 'TypeError'`); `customer_id` is only redacted when it isn't a GUID.
var KEY_HINT = '(?:e-?mail(?:_?address)?|phone(?:_?number|_?home|_?work)?|mobile|tel(?:ephone)?' + '|full_?name|first_?name|last_?name|name_?first|name_?last|business_?name|business_?website' + '|customer_?comments|initial_?prompt|prompt|(?:access_?|refresh_?|id_?)?token|password|passwd|secret' + '|authorization|shopper_?id|user_?id|uid|user_?name|login|customer_?id)';
// An unquoted value runs over spaces and commas until the next `key:`/`key=` or a `;&}]` delimiter, so
// `full_name: Jane Doe, customerComments: call me` loses both values. Only free-text keys redact the
// whole run; for the rest (tokens, ids) the words after the first are kept (`token=abc is invalid`).
var VALUE_WORD = '[^\\s,;&}\\]"\']+';
var UNQUOTED_VALUE = "(?!\\[REDACTED\\])".concat(VALUE_WORD, "(?:(?:[ \\t]+|,[ \\t]*)(?![\"']?[\\w-]+[\"']?\\s*[:=])").concat(VALUE_WORD, ")*");
var KEY_VALUE = new RegExp("(^|[^\\w])([\"']?".concat(KEY_HINT, "[\"']?\\s*[:=]\\s*)(\"(?:[^\"\\\\]|\\\\.)*\"|'[^']*'|").concat(UNQUOTED_VALUE, ")"), 'gi');
var FREE_TEXT_KEY = /name|comments|prompt/i;
var CUSTOMER_ID_KEY = /customer_?id/i;

// Numeric ids in REST paths, e.g. `/v1/shoppers/57944430/...` (numeric shopperIds are PII).
var NUMERIC_ID_PATH = /(\/(?:shoppers?|users?|customers?)\/)(\d{4,})(?=[/?#;&\s"']|$)/gi;

// Phone candidates: 7-15 digits. Bare digit runs are left alone (ids, epoch timestamps, status codes);
// only formatted numbers (leading +, parentheses, or 2+ separator runs) are redacted.
var PHONE_CANDIDATE = /(^|[^\w+\-\u0000])(\+?\(?\d[\d\s().-]{5,}\d)(?![\w-])/g;

// Normalized (lower-case, letters only) object keys whose values are always PII, regardless of type.
// `id` (APM `user.id`, the GUID customerId) is deliberately absent.
var SENSITIVE_KEY_NAMES = new Set(['shopperid', 'userid', 'uid', 'username', 'login', 'loginname', 'email', 'emailaddress', 'recordowneremail', 'phone', 'phonenumber', 'phonehome', 'phonework', 'phonemobile', 'mobile', 'mobilephone', 'telephone', 'fullname', 'firstname', 'lastname', 'namefirst', 'namelast', 'businessname', 'customercomments', 'initialprompt', 'prompt', 'token', 'accesstoken', 'refreshtoken', 'idtoken', 'password', 'passwd', 'secret', 'clientsecret', 'authorization']);
var SENSITIVE_KEY_SUFFIXES = ['email', 'password', 'phonenumber', 'token', 'secret'];
function isGuid(value) {
  return typeof value === 'string' && GUID.test(value);
}
function isSensitiveKey(key) {
  if (typeof key !== 'string') return false;
  var normalized = key.toLowerCase().replace(/[^a-z]/g, '');
  return SENSITIVE_KEY_NAMES.has(normalized) || SENSITIVE_KEY_SUFFIXES.some(function (suffix) {
    return normalized.endsWith(suffix);
  });
}
function redactPhone(match, prefix, candidate) {
  var digits = candidate.replace(/\D/g, '').length;
  var separatorRuns = (candidate.match(/[\s().-]+/g) || []).length;
  var looksFormatted = candidate.startsWith('+') || candidate.includes('(') || separatorRuns >= 2;
  return digits >= 7 && digits <= 15 && looksFormatted ? "".concat(prefix).concat(REDACTED) : match;
}
function redactKeyValue(match, prefix, keyPart, value) {
  // A GUID customerId was protected as a placeholder — keep it; any other value (e.g. numeric) goes.
  if (CUSTOMER_ID_KEY.test(keyPart) && PLACEHOLDER_ONLY.test(value)) return match;
  var quote = value.startsWith('"') || value.startsWith('\'') ? value[0] : '';
  var firstWordEnd = value.search(/[ \t,]/);
  var rest = quote || FREE_TEXT_KEY.test(keyPart) || firstWordEnd === -1 ? '' : value.slice(firstWordEnd);
  return "".concat(prefix).concat(keyPart).concat(quote).concat(REDACTED).concat(quote).concat(rest);
}
function redactTelemetryText(text) {
  if (typeof text !== 'string' || text === '') return text;
  try {
    var result = text.replace(/\u0000/g, '');
    if (result.length > MAX_TEXT_LENGTH) result = "".concat(result.slice(0, MAX_TEXT_LENGTH), "\u2026[TRUNCATED]");
    var protectedValues = [];
    var _iterator = _createForOfIteratorHelper(PROTECTED_PATTERNS),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var pattern = _step.value;
        result = result.replace(pattern, function (_match, prefix, value) {
          protectedValues.push(value);
          return "".concat(prefix, "\0").concat(protectedValues.length - 1, "\0");
        });
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
    result = result.replace(EMAIL, REDACTED).replace(JWT, REDACTED).replace(AUTH_SCHEME, "$1 ".concat(REDACTED)).replace(KEY_VALUE, redactKeyValue).replace(NUMERIC_ID_PATH, "$1".concat(REDACTED)).replace(PHONE_CANDIDATE, redactPhone);
    return result.replace(PLACEHOLDER, function (placeholder, index) {
      var _protectedValues$Numb;
      return (_protectedValues$Numb = protectedValues[Number(index)]) !== null && _protectedValues$Numb !== void 0 ? _protectedValues$Numb : '';
    });
  } catch (_unused) {
    return REDACTED;
  }
}